/**
 * V219 — /platform → tenant → "Usuarios nuevos (soporte)". Los usuarios no se limitan por
 * plan; lo que se limita es CREARLOS: en 30 días móviles, 9 + 5 por sucursal activa adicional
 * + el extra que soporte conceda. El extra CADUCA a los 30 días de otorgarse (volver a guardarlo
 * lo renueva; 0 lo retira). Motivo obligatorio (≥ 10 caracteres); cada cambio queda en la bitácora
 * con su vencimiento y se lista aquí.
 * Formulario sin estado de red (testeable con renderToStaticMarkup); el contenedor carga y envía.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { MIN_REASON, reasonOk } from './OwnerSupportPanel';

export interface UserQuotaInfo {
  used: number;
  cap: number;
  extra: number;
  activeBranches: number;
  nextAt: string | null;
  /** Vence el extra vigente (30 días desde que se otorgó); null = sin extra vigente. */
  extraExpiresAt: string | null;
  history: Array<{ at: string; extra: number; previous: number | null; reason: string; expiresAt: string | null }>;
}

const input = 'w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm';
const btn = 'w-full rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 sm:w-auto';
const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City' });

export const extraOk = (v: string) => /^\d{1,4}$/.test(v.trim()) && Number(v) <= 1000;

export function UserQuotaForm({
  info, busy, error, okMsg, onSave, initial,
}: {
  info: UserQuotaInfo;
  busy: boolean;
  error?: string | null;
  okMsg?: string | null;
  onSave: (v: { extra: number; reason: string }) => void;
  initial?: { extra: string; reason: string };
}) {
  const [extra, setExtra] = useState(initial?.extra ?? String(info.extra));
  const [reason, setReason] = useState(initial?.reason ?? '');
  const base = info.cap - info.extra;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-600">
        Últimos 30 días: <strong className="text-slate-800">{info.used} de {info.cap}</strong> usuarios nuevos
        {' '}(9 + 5 × {Math.max(0, info.activeBranches - 1)} sucursal(es) adicional(es){info.extra ? ` + ${info.extra} de soporte` : ''}).
      </p>
      {info.extra > 0 && info.extraExpiresAt && (
        <p className="text-xs text-slate-600">
          El extra de {info.extra} vence el <strong className="text-slate-800">{fecha(info.extraExpiresAt)}</strong>.
        </p>
      )}
      {info.nextAt && (
        <p className="rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Llegó al tope: podrá crear más el {fecha(info.nextAt)}.
        </p>
      )}
      <div className="space-y-2">
        <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-400" htmlFor="quota-extra">
          Extra de soporte por 30 días (se suma a {base})
        </label>
        <input id="quota-extra" className={input} inputMode="numeric" value={extra} onChange={(e) => setExtra(e.target.value)} />
        <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-400" htmlFor="quota-reason">
          Motivo (obligatorio, mínimo {MIN_REASON} caracteres)
        </label>
        <textarea id="quota-reason" className={input} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        <button
          type="button"
          className={`${btn} bg-slate-900 text-white`}
          // Mismo número que el vigente = renovar otros 30 días; 0 sin extra vigente no hace nada.
          disabled={busy || !extraOk(extra) || (Number(extra) === 0 && info.extra === 0) || !reasonOk(reason)}
          onClick={() => onSave({ extra: Number(extra), reason: reason.trim() })}
        >
          {busy ? 'Guardando…' : 'Guardar tope'}
        </button>
      </div>
      {info.history.length > 0 && (
        <ul className="space-y-1 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          {info.history.map((h, i) => (
            <li key={i}>
              {fecha(h.at)} · extra {h.previous ?? 0} → {h.extra}{h.expiresAt ? ` · vence ${fecha(h.expiresAt)}` : ''} · {h.reason}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {okMsg && <p className="text-xs text-emerald-700">{okMsg}</p>}
    </div>
  );
}

/** Contenedor: carga GET /platform/tenants/:id/user-quota y guarda el extra. */
export function UserQuotaPanel({ tenantId }: { tenantId: string }) {
  const [info, setInfo] = useState<UserQuotaInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  const load = useCallback(async () => {
    try { setInfo(await api<UserQuotaInfo>(`/platform/tenants/${tenantId}/user-quota`)); }
    catch (e: any) { setError(e?.body?.message || e?.message || 'No se pudo cargar el tope.'); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const save = async (v: { extra: number; reason: string }) => {
    setBusy(true); setError(null); setOkMsg(null);
    try {
      await api(`/platform/tenants/${tenantId}/user-quota`, { method: 'POST', body: v });
      setOkMsg('Tope actualizado. Quedó en la bitácora.');
      await load(); setRev((r) => r + 1);
    } catch (e: any) {
      const m = e?.body?.message;
      setError(Array.isArray(m) ? m.join(' · ') : (m || e?.message || 'No se pudo guardar.'));
    } finally { setBusy(false); }
  };

  if (!info) return <p className="text-xs text-slate-400">{error ?? 'Cargando…'}</p>;
  return <UserQuotaForm key={rev} info={info} busy={busy} error={error} okMsg={okMsg} onSave={save} />;
}
